import React, { useState, useEffect } from 'react';
import { Address } from '../../dto/Address.dto';
// import { X } from 'lucide-react';
import './style/NewAddressModal.css';
import NewAddressModalMobile from './NewAddressModal.mobile';
import { ProfileService } from '../../service/Profile.service';
import { ApiService } from '../../../../shared/service/Apiservice';
import { LocationService, AutocompletePrediction } from '../../../../services/location.api.service';
import axios from 'axios';
import MapComponent from './MapComponent';
interface NewAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddAddress?: (newAddress: Address, addressId?: string) => void;
  addressToEdit?: Address | null;
}

function isMobileDevice() {
  if (typeof window !== 'undefined') {
    return window.innerWidth <= 600;
  }
  return false;
}

const initialFormState = {
  fullName: '',
  addressLabel: 'Home',
  phoneNumber: '',
  searchAddress: '',
  floor: '',
  landmark: '',
  name: '',
  locality: ''
};

const NewAddressModal = ({ isOpen, onClose, onAddAddress, addressToEdit }: NewAddressModalProps) => {
  const [formData, setFormData] = useState(initialFormState);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [suggestions, setSuggestions] = useState<AutocompletePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [reverseGeocodedData, setReverseGeocodedData] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [mapCoordinates, setMapCoordinates] = useState({ latitude: 28.6139, longitude: 77.2090 }); // Default to New Delhi

  useEffect(() => {
    if (addressToEdit && isOpen) {
      setFormData({
        fullName: addressToEdit.primary_address_line || '',
        addressLabel: addressToEdit.label || 'Home',
        phoneNumber: addressToEdit.phone_number || '',
        searchAddress: addressToEdit.locality || '',
        floor: addressToEdit.secondary_address_line || '',
        landmark: addressToEdit.landmark || '',
        name: addressToEdit.name || '',
        locality: addressToEdit.locality || addressToEdit.primary_address_line || '',
      });
      const lat = (addressToEdit as any).latitude ?? (addressToEdit as any).lat;
      const lng = (addressToEdit as any).longitude ?? (addressToEdit as any).lng;
      if (typeof lat === 'number' && typeof lng === 'number') {
        setMapCoordinates({ latitude: lat, longitude: lng });
      }
      setError(null);
      setSuggestions([]);
      setShowSuggestions(false);
    }

    if (!addressToEdit && isOpen) {
      setFormData(initialFormState);
      setError(null);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [addressToEdit, isOpen]);

  // Create API service instances
  const apiService = React.useMemo(() => new ApiService(axios.create()), []);
  const profileService = React.useMemo(() => new ProfileService(apiService), [apiService]);
  const locationService = React.useMemo(() => new LocationService(), []);

  // Fetch user phone number and current location when modal opens
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await profileService.getUserProfile();
        if (response.success && response.user?.phone) {
          setFormData(prev => ({
            ...prev,
            phoneNumber: response.user?.phone?.replace(/^\+91/, '') || ''
          }));
        }
      } catch (err) {
      }
    };

    const fetchCurrentLocation = async () => {
      if (navigator.geolocation) {
        setIsLoadingLocation(true);
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 300000 // 5 minutes
            });
          });

          const { latitude, longitude } = position.coords;
          
          // Update map coordinates
          setMapCoordinates({ latitude, longitude });
          
          // Reverse geocode the coordinates to get location name
          const response = await locationService.reverseGeocode(latitude, longitude);
          if (response.success && response.data) {
            const data = response.data;
            // Store the reverse geocoded data for auto-fill when "Home" is selected
            setReverseGeocodedData(data);
            
            // Auto-fill the form data with the reverse geocoded location
            setFormData(prev => ({
              ...prev,
              fullName: data.formattedAddress.split(',').slice(0, -3).join(','),
              searchAddress: data.locationName,
              locality: data.locality || data.sublocality || data.area || data.city || ''
            }));
          }
        } catch (err) {
        } finally {
          setIsLoadingLocation(false);
        }
      }
    };

    if (isOpen) {
      fetchUserProfile();
      if (!addressToEdit) {
        fetchCurrentLocation();
      }
    }
  }, [isOpen, profileService, locationService, addressToEdit]);

  // Handle address label change
  const handleAddressLabelChange = (label: string) => {
    setFormData(prev => ({
      ...prev,
      addressLabel: label
    }));
    
    // If Home is selected and we have reverse geocoded data, auto-fill
    if (label === 'Home' && reverseGeocodedData) {
      setFormData(prev => ({
        ...prev,
        addressLabel: label,
        fullName: reverseGeocodedData.formattedAddress.split(',').slice(0, -3).join(','),
        searchAddress: reverseGeocodedData.locationName,
        locality: reverseGeocodedData.locality || reverseGeocodedData.sublocality || reverseGeocodedData.area || ''
      }));
      setSuggestions([]);
      setShowSuggestions(false);
    } else {
      // Clear suggestions for other types
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Get suggestions based on input
  // fieldName: 'searchAddress' or 'fullName'
  const handleSearchAddressChange = async (value: string, fieldName: string = 'searchAddress') => {
    // Update value but intentionally do not fetch or show suggestions
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    setIsLoadingSuggestions(false);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Handle suggestion click
  const handleSuggestionSelect = async (prediction: AutocompletePrediction) => {
    // Close the suggestions dropdown immediately
    setSuggestions([]);
    setShowSuggestions(false);

    // Get detailed place information and fill all fields
    try {
      const response = await locationService.getPlaceDetails(prediction.placeId);
      if (response.success && response.data) {
        const data = response.data;
        setFormData(prev => ({
          ...prev,
          fullName: data.formattedAddress || prediction.description,
          searchAddress: data.locationName || prediction.mainText || prediction.description,
          locality: data.locality || data.sublocality || data.area || data.city || ''
        }));
      } else {
        // Fallback if place details fail
        setFormData(prev => ({
          ...prev,
          fullName: prediction.description,
          searchAddress: prediction.mainText || prediction.description
        }));
      }
    } catch (err) {
      // Fallback on error
      setFormData(prev => ({
        ...prev,
        fullName: prediction.description,
        searchAddress: prediction.mainText || prediction.description
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    // Do not show suggestions for flat/house/building field; treat as plain text
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Suggestions are only for the area/sector/locality search field
    if (name === 'searchAddress') {
      handleSearchAddressChange(value, name);
    }
  };

  const handleAddAddress = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isSaving) return;
    setError(null);
    // Basic validation
    if (!formData.fullName || !formData.searchAddress || !formData.name || !formData.locality) {
      setError('Please fill all required fields.');
      return;
    }
    if (formData.phoneNumber && formData.phoneNumber.length !== 10) {
      setError('Phone number must be 10 digits.');
      return;
    }
    const newAddress: Address = {
      label: formData.addressLabel as 'Home' | 'Work' | 'Other',
      name: formData.name || formData.fullName,
      phone_number: formData.phoneNumber,
      primary_address_line: formData.fullName,
      secondary_address_line: formData.floor,
      locality: formData.locality || formData.searchAddress,
      landmark: formData.landmark,
      latitude: mapCoordinates.latitude,
      longitude: mapCoordinates.longitude,
    };
    try {
      setIsSaving(true);
      if (onAddAddress) {
        await onAddAddress(newAddress, addressToEdit?._id);
      }
      onClose();
      setFormData(initialFormState);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsSaving(false);
    }
  };
  const handleCancel = () => {
    onClose();
    setFormData(initialFormState);
    setError(null);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  if (!isOpen) return null;

  if (isMobileDevice()) {
    return (
      <NewAddressModalMobile isOpen={isOpen} onClose={onClose} onAddAddress={onAddAddress} addressToEdit={addressToEdit} />
    );
  }

  return (
    <>
      <div className="modal-overlay" onClick={handleCancel}></div>
      <div className="modal-container modal-with-map">
        <div className="modal-header">
          <h2 className="modal-title">{addressToEdit ? 'Edit Address' : 'Add New Address'}</h2>
          <button className="close-btn" onClick={handleCancel}>×</button>
        </div>
        <div className="modal-body modal-body-with-map">
          {/* Map Section - Left Side */}
          <div className="map-section">
            <MapComponent 
              latitude={mapCoordinates.latitude} 
              longitude={mapCoordinates.longitude}
              locationName={formData.searchAddress || 'Your Location'}
              onLocationChange={(lat, lng) => setMapCoordinates({ latitude: lat, longitude: lng })}
            />
          </div>

          {/* Form Section - Right Side */}
          <div className="form-section">
            <div className="address-type-row">
              {['Home', 'Work', 'Other'].map(type => (
                <button
                  key={type}
                  className={`address-type-btn${formData.addressLabel === type ? ' selected' : ''}`}
                  onClick={() => handleAddressLabelChange(type)}
                >
                  {type === 'Home' && <span role="img" aria-label="Home">🏠</span>}
                  {type === 'Work' && <span role="img" aria-label="Work">🏢</span>}
                  {type === 'Other' && <span role="img" aria-label="Other">🔖</span>}
                  <span className="address-type-label">{type}</span>
                </button>
              ))}
            </div>
            <form onSubmit={handleAddAddress}>
              <div className="form-grid">
              <div className="form-group" style={{ position: 'relative' }}>
                <label className="form-label">Flat / House no / Building name <span className="required">*</span></label>
                <input type="text" name="fullName" className="form-input" placeholder="Flat / House no / Building name" required value={formData.fullName} onChange={handleInputChange} />
                
                {/* No suggestions for Flat/House field */}
                {false && showSuggestions && suggestions.length > 0 && formData.addressLabel !== 'Home' && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderTop: 'none',
                    borderRadius: '0 0 4px 4px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                    {suggestions.map((suggestion) => (
                      <div
                        key={suggestion.placeId}
                        onClick={() => handleSuggestionSelect(suggestion)}
                        style={{
                          padding: '10px 12px',
                          borderBottom: '1px solid #eee',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <div style={{ fontWeight: '500', color: '#333' }}>
                          {suggestion.mainText}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {suggestion.secondaryText}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Floor (optional)</label>
                <input type="text" name="floor" className="form-input" placeholder="Floor" value={formData.floor} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Area / Sector / Locality <span className="required">*</span></label>
                <div style={{ position: 'relative' }}>
                  <input 
                    type="text" 
                    name="searchAddress" 
                    className="form-input" 
                    placeholder={formData.addressLabel === 'Home' ? (isLoadingLocation ? "Detecting your location..." : "Your location") : "Start typing to search for location..."} 
                    required 
                    value={formData.searchAddress} 
                    onChange={(e) => setFormData(prev => ({ ...prev, searchAddress: e.target.value }))}
                    disabled={isLoadingLocation || (formData.addressLabel === 'Home' && isLoadingLocation)}
                    readOnly={formData.addressLabel === 'Home' && isLoadingLocation}
                  />
                  {isLoadingLocation && (
                    <div style={{ 
                      position: 'absolute', 
                      right: '10px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      color: '#666'
                    }}>
                      📍
                    </div>
                  )}
                  {isLoadingSuggestions && formData.addressLabel !== 'Home' && (
                    <div style={{ 
                      position: 'absolute', 
                      right: '10px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      color: '#666'
                    }}>
                      ⏳
                    </div>
                  )}
                  
                  {/* No suggestions for Area/Sector/Locality field */}
                  {false && showSuggestions && suggestions.length > 0 && formData.addressLabel !== 'Home' && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}>
                      {suggestions.map((suggestion) => (
                        <div
                          key={suggestion.placeId}
                          onClick={() => handleSuggestionSelect(suggestion)}
                          style={{
                            padding: '10px 12px',
                            borderBottom: '1px solid #eee',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            fontSize: '14px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <div style={{ fontWeight: '500', color: '#333' }}>
                            {suggestion.mainText}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {suggestion.secondaryText}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Nearby landmark (optional)</label>
                <input type="text" name="landmark" className="form-input" placeholder="Nearby landmark" value={formData.landmark} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Your name <span className="required">*</span></label>
                <input type="text" name="name" className="form-input" placeholder="Your name" required value={formData.name} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Your phone number (optional)</label>
                <input type="tel" name="phoneNumber" className="form-input" placeholder="Your phone number" maxLength={10} value={formData.phoneNumber} onChange={handleInputChange} />
              </div>
              </div>
              {error && (
                <div style={{ color: '#d32f2f', marginBottom: '10px', fontSize: '14px' }}>{error}</div>
              )}
              <div className="modal-actions">
                <button className="cancel-btn" type="button" onClick={handleCancel} disabled={isSaving}>Cancel</button>
                <button className="add-btn" type="submit" disabled={isSaving}>{addressToEdit ? 'Update Address' : (isSaving ? 'Saving...' : 'Save Address')}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewAddressModal;